import { Injectable } from '@angular/core';
import { finalize, map, Observable, of, switchMap, tap } from 'rxjs';
import { PropertyMap, SalesComparison, ValuationType } from 'src/app/module/order/interface/order.interface';
import { ApproachFactoryState } from 'src/app/module/order/state/approach-factory.state';
import {
  ExtendedPropertyDetail,
  PropertyAttribute,
  PropertyAttributeValue,
  PropertyDetail,
} from 'src/app/module/property/interface/property.interface';
import { PropertyService } from 'src/app/module/property/service/property.service';
import { PropertyState } from 'src/app/module/property/state/property.state';
import { OrderMultiDataSourcePipe } from 'src/app/shared/pipes/order-multi-data-source.pipe';
import { SelectedSourceDataPipe } from 'src/app/shared/pipes/selected-source-data.pipe';
import { ProgressLoaderService } from 'src/app/shared/services/progress-loader.service';
import { SnackBarService } from 'src/app/shared/services/snack-bar.service';
import { CompanyState } from 'src/app/shared/state/company.state';
import { ValueAdjustmentGridConfig } from '../../adjustment-grid/interface/adjustment.interface';
import { ScenarioState } from '../../scenario/state/scenario.state';
import { SyncScope, SyncUpdateScope } from '../../sync/interface/sync.interface';
import { SyncService } from '../../sync/service/sync.service';
import { OrderState } from '../state/order.state';
import { ComparableService } from './comparable.service';
import { OrderService } from './order.service';

@Injectable({
  providedIn: 'root',
})
export class ApproachFactoryService {
  constructor(
    private approachFactoryState: ApproachFactoryState,
    private comparableService: ComparableService,
    private companyState: CompanyState,
    private orderMultiDataSourcePipe: OrderMultiDataSourcePipe,
    private orderService: OrderService,
    private orderState: OrderState,
    private progressLoaderService: ProgressLoaderService,
    private propertyService: PropertyService,
    private propertyState: PropertyState,
    private scenarioState: ScenarioState,
    private selectedSourceDataPipe: SelectedSourceDataPipe,
    private snackBarService: SnackBarService,
    private syncService: SyncService
  ) {}

  public recalculateAndUpdateProperties(map?: PropertyMap) {
    switch (this.orderState.activeValuationType) {
      case ValuationType.SALES:
        this._recalculateAndUpdateSalesProperties(map);
        break;

      case ValuationType.COST:
        break;

      case ValuationType.SHORT_TERM_RENTAL:
        this._recalculateAndUpdateStrProperties(map);
        break;

      case ValuationType.LONG_TERM_RENTAL:
        this._recalculateAndUpdateLtrProperties(map);
        break;

      default:
        break;
    }
  }

  public deselectSelectedComparable(comparableId: string): Observable<boolean> {
    const selectedCompList = this.approachFactoryState.getOrderValue('selectedComparableList');
    const updatedSelectedCompList = selectedCompList?.filter((selectedComp) => selectedComp.id != comparableId) ?? null;

    /** Updating order instantly without sync delay */
    const loaderId = this.progressLoaderService.show('Deleting Comparable');
    return this.orderService
      .updateOrder([
        {
          scope: SyncUpdateScope.SCENARIO,
          id: this.scenarioState.activeScenario?.id,
          jsonPath: `${this.approachFactoryState.activeApproachKey}.selectedComparableList`,
          value: updatedSelectedCompList,
        },
      ])
      .pipe(
        switchMap((success) => {
          if (!success) {
            this.snackBarService.open('error', 'Failed to delete the comp. Please refresh and try again.');
            return of(false);
          }

          const activeScenario = structuredClone(this.scenarioState.activeScenario);
          activeScenario![this.approachFactoryState.activeApproachKey]!.selectedComparableList =
            updatedSelectedCompList;
          this.syncService.syncLocally(SyncScope.SCENARIO, activeScenario);

          const loaderId = this.progressLoaderService.show('Updating Order');

          return this.comparableService
            .fetchSelectedComparables(this.scenarioState.activeScenario!.id, this.orderState.activeValuationType!)
            .pipe(
              tap(() => {
                this.recalculateAndUpdateProperties();

                this.progressLoaderService.hide(loaderId);
              }),
              map(() => true)
            );
        }),
        finalize(() => this.progressLoaderService.hide(loaderId))
      );
  }

  private _recalculateAndUpdateSalesProperties(map?: PropertyMap): void {
    const activeScenario = structuredClone(this.scenarioState.activeScenario);
    const subjectProperty = structuredClone(this.propertyState.subjectPropertyData) as PropertyDetail;
    const comparableMap = map ?? this.approachFactoryState.selectedComparableMap;
    let derivedPriceSum = 0;

    // Compute comparables Adjustment
    for (const propertyId in comparableMap) {
      const comparable = comparableMap[propertyId] as PropertyDetail;

      let totalAdjustmentValue: number = 0;
      let totalGrossAdjustmentValue: number = 0;

      for (const [key, characteristic] of Object.entries(comparable)) {
        const ignoreAutoAdjustment = this.companyState.getPropertyFieldConfig(this.orderState.reportType)[
          key as PropertyAttribute
        ]?.ignoreAutoAdjustment;
        totalAdjustmentValue +=
          Number(this.orderMultiDataSourcePipe.transform(characteristic?.adjustmentValue, ignoreAutoAdjustment)) || 0;
        const grossAdjustmentValue =
          Number(this.orderMultiDataSourcePipe.transform(characteristic?.adjustmentValue, ignoreAutoAdjustment)) || 0;
        totalGrossAdjustmentValue += Math.abs(grossAdjustmentValue);
      }

      const structureUnitAdj = this._sumStructureAndUnitAdjustments(comparableMap[propertyId]);
      totalAdjustmentValue += structureUnitAdj.total;
      totalGrossAdjustmentValue += structureUnitAdj.gross;

      comparableMap[propertyId].adjustmentValue = {
        userRecord: Number(totalAdjustmentValue) ?? 0,
        selectedSource: 'userRecord',
      } as PropertyAttributeValue;

      comparableMap[propertyId].grossAdjustmentValue = {
        userRecord: Number(totalGrossAdjustmentValue) ?? 0,
        selectedSource: 'userRecord',
      } as PropertyAttributeValue;

      const { price } = this.propertyService.getCurrentStatusAndPrice(comparableMap[propertyId]);

      // Compute Comparable Price
      const totalAdjustedPrice = Number(price ?? 0) + totalAdjustmentValue;
      comparableMap[propertyId].adjustedPrice = {
        userRecord: totalAdjustedPrice,
        selectedSource: 'userRecord',
      } as PropertyAttributeValue;

      const livingArea = Number(
        (this.selectedSourceDataPipe.transform(comparableMap[propertyId].LivingArea) as string) ?? 0
      );
      const perSqFtPrice = Math.round(livingArea ? Number(price) / livingArea : 0);
      const adjustedPerSqFtPrice = Math.round(livingArea ? totalAdjustedPrice / livingArea : 0);

      comparableMap[propertyId].pricePerSquareFeet = {
        userRecord: perSqFtPrice,
        selectedSource: 'userRecord',
      } as PropertyAttributeValue;

      comparableMap[propertyId].adjustedPricePerSquareFeet = {
        userRecord: adjustedPerSqFtPrice,
        selectedSource: 'userRecord',
      } as PropertyAttributeValue;

      derivedPriceSum +=
        totalAdjustedPrice *
        (this.selectedSourceDataPipe.transform(comparableMap[propertyId].weightage) ??
          100 / Object.keys(comparableMap).length);
    }

    // Update comparable adjustments and price
    this.approachFactoryState.selectedComparableMap = comparableMap;

    // Compute Subject Property price
    const livingArea = Number((this.selectedSourceDataPipe.transform(subjectProperty.LivingArea) as string) ?? 0);
    const derivedPricePerSqft = livingArea ? Math.round(derivedPriceSum / 100 / livingArea) : 0;

    if (this.approachFactoryState.activeApproachKey && activeScenario?.[this.approachFactoryState.activeApproachKey]) {
      (activeScenario[this.approachFactoryState.activeApproachKey] as SalesComparison).derivedPrice = Math.round(
        derivedPriceSum / 100
      );
      (activeScenario[this.approachFactoryState.activeApproachKey] as SalesComparison).derivedPricePerSqft =
        derivedPricePerSqft;
    }

    this.scenarioState.activeScenario = activeScenario;
  }

  private _recalculateAndUpdateStrProperties(map?: PropertyMap): void {
    const activeScenario = structuredClone(this.scenarioState.activeScenario);
    const comparableMap = map ?? this.approachFactoryState.selectedComparableMap;
    let derivedMonthlyRentSum = 0;

    //Filtering syncCompsWithSubject fields from value adjustment layout config
    const valueAdjustmentConfig: ValueAdjustmentGridConfig =
      this.approachFactoryState.activeApproachSetting!.valueAdjustmentLayout;

    const propertySyncFieldList: Array<PropertyAttribute> = valueAdjustmentConfig
      .map((layout) =>
        layout.children.filter((config) => config.syncCompsWithSubject).map((item) => item.propertyFieldList)
      )
      .flat()
      .flat();

    // Compute comparables Adjustment
    for (const propertyId in comparableMap) {
      const comparable = comparableMap[propertyId] as PropertyDetail;

      let totalMonthlyAdjustment: number = 0;
      let totalGrossAdjustMentValue: number = 0;
      for (const [key, characteristic] of Object.entries(comparable)) {
        //updating comps with subject for syncCompsWithSubject enabled fields
        if (propertySyncFieldList.includes(key as PropertyAttribute)) {
          comparableMap[propertyId][key as PropertyAttribute]!.selectedSource = 'userRecord';
          comparableMap[propertyId][key as PropertyAttribute]!.userRecord = this.selectedSourceDataPipe.transform(
            this.propertyState.subjectPropertyData?.[key as PropertyAttribute]
          );
          comparableMap[propertyId][key as PropertyAttribute]!.adjustmentValue = {
            autoComputed: null,
            userEntered: null,
          };
        }

        const ignoreAutoAdjustment = this.companyState.getPropertyFieldConfig(this.orderState.reportType)[
          key as PropertyAttribute
        ]?.ignoreAutoAdjustment;

        totalMonthlyAdjustment +=
          Number(this.orderMultiDataSourcePipe.transform(characteristic?.adjustmentValue, ignoreAutoAdjustment)) || 0;
        const grossAdjustmentValue =
          Number(this.orderMultiDataSourcePipe.transform(characteristic?.adjustmentValue, ignoreAutoAdjustment)) || 0;
        totalGrossAdjustMentValue += Math.abs(grossAdjustmentValue);
      }

      const structureUnitAdj = this._sumStructureAndUnitAdjustments(comparableMap[propertyId]);
      totalMonthlyAdjustment += structureUnitAdj.total;
      totalGrossAdjustMentValue += structureUnitAdj.gross;

      comparableMap[propertyId].adjustmentValue = {
        userRecord: Number(totalMonthlyAdjustment) ?? 0,
        selectedSource: 'userRecord',
      } as PropertyAttributeValue;

      comparableMap[propertyId].grossAdjustmentValue = {
        userRecord: Number(totalGrossAdjustMentValue) ?? 0,
        selectedSource: 'userRecord',
      } as PropertyAttributeValue;

      const dailyRent = Number(this.selectedSourceDataPipe.transform(comparableMap[propertyId].ListPriceNightly));

      const monthlyRent = (dailyRent * 365) / 12;

      const occupancyRate = Number(this.selectedSourceDataPipe.transform(comparableMap[propertyId].OccupancyRate));

      let monthlyRentByOccupancyRate = monthlyRent;

      if (![null, undefined, NaN, ''].includes(occupancyRate)) {
        monthlyRentByOccupancyRate = monthlyRent * (occupancyRate / 100);
      }

      let totalMonthlyAdjustedRent = 0;

      totalMonthlyAdjustedRent = Number(monthlyRentByOccupancyRate ?? 0) + totalMonthlyAdjustment;

      comparableMap[propertyId].adjustedPrice = {
        userRecord: totalMonthlyAdjustedRent,
        selectedSource: 'userRecord',
      } as PropertyAttributeValue;

      derivedMonthlyRentSum +=
        totalMonthlyAdjustedRent *
        (this.selectedSourceDataPipe.transform(comparableMap[propertyId].weightage) ??
          100 / Object.keys(comparableMap).length);
    }

    // Update comparable adjustments and price
    this.approachFactoryState.selectedComparableMap = comparableMap;

    // Compute Subject Property price
    activeScenario!.shortTermRental!.derivedMonthlyRent = Math.round(derivedMonthlyRentSum / 100);

    this.scenarioState.activeScenario = activeScenario;
  }

  private _recalculateAndUpdateLtrProperties(map?: PropertyMap): void {
    const activeScenario = structuredClone(this.scenarioState.activeScenario);
    const comparableMap = map ?? this.approachFactoryState.selectedComparableMap;
    let derivedMonthlyRentSum = 0;

    // Compute comparables Adjustment
    for (const propertyId in comparableMap) {
      const comparable = comparableMap[propertyId] as PropertyDetail;

      let totalMonthlyAdjustment: number = 0;
      let totalGrossAdjustMentValue: number = 0;

      for (const [key, characteristic] of Object.entries(comparable)) {
        const ignoreAutoAdjustment = this.companyState.getPropertyFieldConfig(this.orderState.reportType)[
          key as PropertyAttribute
        ]?.ignoreAutoAdjustment;

        totalMonthlyAdjustment +=
          Number(this.orderMultiDataSourcePipe.transform(characteristic?.adjustmentValue, ignoreAutoAdjustment)) || 0;
        const grossAdjustmentValue =
          Number(this.orderMultiDataSourcePipe.transform(characteristic?.adjustmentValue, ignoreAutoAdjustment)) || 0;
        totalGrossAdjustMentValue += Math.abs(grossAdjustmentValue);
      }

      const structureUnitAdj = this._sumStructureAndUnitAdjustments(comparableMap[propertyId]);
      totalMonthlyAdjustment += structureUnitAdj.total;
      totalGrossAdjustMentValue += structureUnitAdj.gross;

      comparableMap[propertyId].adjustmentValue = {
        userRecord: Number(totalMonthlyAdjustment) ?? 0,
        selectedSource: 'userRecord',
      } as PropertyAttributeValue;

      comparableMap[propertyId].grossAdjustmentValue = {
        userRecord: Number(totalGrossAdjustMentValue) ?? 0,
        selectedSource: 'userRecord',
      } as PropertyAttributeValue;

      const monthlyRent =
        this.propertyService.getCurrentStatusAndPrice(comparable).price ??
        Number(this.selectedSourceDataPipe.transform(comparable?.ListPrice));

      let totalMonthlyAdjustedRent = 0;

      totalMonthlyAdjustedRent = Number(monthlyRent ?? 0) + totalMonthlyAdjustment;

      comparableMap[propertyId].adjustedPrice = {
        userRecord: totalMonthlyAdjustedRent,
        selectedSource: 'userRecord',
      } as PropertyAttributeValue;

      derivedMonthlyRentSum +=
        totalMonthlyAdjustedRent *
        (this.selectedSourceDataPipe.transform(comparableMap[propertyId].weightage) ??
          100 / Object.keys(comparableMap).length);
    }

    // Update comparable adjustments and price
    this.approachFactoryState.selectedComparableMap = comparableMap;

    // Compute Subject Property price
    activeScenario!.longTermRental!.derivedMonthlyRent = Math.round(derivedMonthlyRentSum / 100);

    this.scenarioState.activeScenario = activeScenario;
  }

  private _sumStructureAndUnitAdjustments(comparable: ExtendedPropertyDetail): { total: number; gross: number } {
    let total = 0;
    let gross = 0;

    for (const structure of comparable.structures ?? []) {
      for (const [key, characteristic] of Object.entries(structure)) {
        if (key === 'units') continue;
        const fieldValue = characteristic as PropertyAttributeValue;
        const ignoreAutoAdjustment = this.companyState.getPropertyFieldConfig(this.orderState.reportType)[
          key as PropertyAttribute
        ]?.ignoreAutoAdjustment;
        const adj =
          Number(this.orderMultiDataSourcePipe.transform(fieldValue?.adjustmentValue, ignoreAutoAdjustment)) || 0;
        total += adj;
        gross += Math.abs(adj);
      }

      for (const unit of structure.units ?? []) {
        for (const [key, characteristic] of Object.entries(unit)) {
          if (typeof characteristic !== 'object' || !characteristic) continue;
          const fieldValue = characteristic;
          const ignoreAutoAdjustment = this.companyState.getPropertyFieldConfig(this.orderState.reportType)[
            key as PropertyAttribute
          ]?.ignoreAutoAdjustment;
          const adj =
            Number(this.orderMultiDataSourcePipe.transform(fieldValue?.adjustmentValue, ignoreAutoAdjustment)) || 0;
          total += adj;
          gross += Math.abs(adj);
        }
      }
    }

    return { total, gross };
  }
}
